import dayjs from 'dayjs';

import { DebtProductDetails, DebtProductRowData } from '../../../DebtProducts/debtProductTypes';

/**
 * Resolves the API `referenceId` for GET /getDealProbability
 * from the current row's `dealId` only.
 */
export function resolveInvestorDealReferenceId(row: any): string | undefined {
    if (!row?.dealId) return undefined;

    const id = String(row.dealId).trim();

    return id !== '' ? id : undefined;
}

/**
 * Gets probability from row (used in table display).
 * Prefers normalized `dealProbability`, then `customFields.optionProbability`.
 */
export function getOptionProbabilityForDisplay(row: any): number | null | undefined {
    const fromDeal = row?.dealProbability;
    if (fromDeal !== null && fromDeal !== undefined) {
        const n = typeof fromDeal === 'number' ? fromDeal : Number(fromDeal);
        if (!Number.isNaN(n)) return n;
    }
    return row?.customFields?.optionProbability;
}

/**
 * Numeric value for sorting / range filtering (desc sort, client-side filter).
 * Prefers `dealProbability`, then `customFields.optionProbability`.
 * `0` is valid. `null` / `undefined` / invalid → `-1` (appears last when sorting; excluded when filtering by range).
 */
export function getDealRowProbabilityForSort(row: any): number {
    const fromDeal = row?.dealProbability;
    const p =
        fromDeal !== null && fromDeal !== undefined && !Number.isNaN(Number(fromDeal)) ? fromDeal : row?.customFields?.optionProbability;
    if (p === null || p === undefined) return -1;
    const n = typeof p === 'number' ? p : Number(p);
    if (Number.isNaN(n)) return -1;
    return n;
}

/**
 * Stable descending sort by option probability (higher first). Does not mutate `rows`.
 */
export function sortDealRowsByProbabilityDesc<T>(rows: T[] | null | undefined): T[] {
    if (!rows?.length) return rows ? [...rows] : [];
    return [...rows]
        .map((row, index) => ({ row, index }))
        .sort((a, b) => {
            const probA = getDealRowProbabilityForSort(a.row);
            const probB = getDealRowProbabilityForSort(b.row);
            if (probB !== probA) return probB - probA;
            return a.index - b.index;
        })
        .map(({ row }) => row);
}

/**
 * Builds `filters.dealProbability` for getAllLenderDeals / getAllDeals from drawer state
 * (`RangeFilterRenderer` → `{ operator: 'BETWEEN', value: { gte?, lte? } }`).
 */
export function normalizeDealProbabilityFilterForApi(
    dealProbability: unknown,
): { operator: 'BETWEEN'; value: { gte?: number; lte?: number } } | undefined {
    if (dealProbability == null || Array.isArray(dealProbability)) return undefined;
    if (typeof dealProbability !== 'object') return undefined;
    const f = dealProbability as { operator?: string; value?: { gte?: number | null; lte?: number | null } };
    if (f.operator !== 'BETWEEN' || !f.value) return undefined;
    const { gte, lte } = f.value;
    const hasMin = gte !== null && gte !== undefined && String(gte) !== '' && !Number.isNaN(Number(gte));
    const hasMax = lte !== null && lte !== undefined && String(lte) !== '' && !Number.isNaN(Number(lte));
    if (!hasMin && !hasMax) return undefined;
    return {
        operator: 'BETWEEN',
        value: {
            ...(hasMin ? { gte: Number(gte) } : {}),
            ...(hasMax ? { lte: Number(lte) } : {}),
        },
    };
}

/**
 * `responseBody` from GET /getDealProbability (no other fields assumed).
 */
export type DealProbabilityApiBody = {
    fileName?: string;
    productId?: string;
    probability?: number | null;
    details?: DebtProductDetails;
    referenceId?: string;
    /** Epoch ms */
    createdAt?: number | null;
    dealSharedBy?: string | null;
};

/**
 * Header line for deal probability drawer: "As of <time>, <date> by <user>"
 */
export function formatDealProbabilityTimelineText(data: Pick<DebtProductRowData, 'dealProbabilityCreatedAt' | 'dealSharedBy'>): string {
    const name = data.dealSharedBy?.trim() ? data.dealSharedBy.trim() : '—';
    const ts = data.dealProbabilityCreatedAt;
    if (ts == null || !dayjs(ts).isValid()) {
        return `As of — by ${name}`;
    }
    return `As of ${dayjs(ts).format('h:mm a, D MMM YYYY')} by ${name}`;
}

/**
 * Format productId → readable name
 */
export function formatDealProbabilityProductName(productId?: string): string {
    if (!productId) return '-';
    return productId.replace(/_/g, ' ');
}

export function mapDealProbabilityResponseToDebtProductRow(body: DealProbabilityApiBody | null | undefined): DebtProductRowData {
    if (!body) {
        return {
            productName: '-',
            productId: '',
            probability: '0',

            dataPending: '',
            amount: '',
            tenure: '',
            roi: '',
            timeline: '',
            security: [],
            isNegativeLender: false,

            lenders: [],

            aggregatorId: '',
            details: { terms: {}, eligibility: {} },
        };
    }

    const prob = body.probability;

    const probabilityStr = prob != null && !Number.isNaN(Number(prob)) ? String(Math.round(Number(prob))) : '0';

    const details = body.details ?? { terms: {}, eligibility: {} };

    const baseRow: Omit<DebtProductRowData, 'lenders'> = {
        productName: formatDealProbabilityProductName(body.productId),
        productId: body.productId || '',
        probability: probabilityStr,

        dataPending: '',
        amount: '',
        tenure: '',
        roi: '',
        timeline: '',
        security: [],
        isNegativeLender: false,

        dealProbabilityCreatedAt: body.createdAt ?? null,
        dealSharedBy: body.dealSharedBy ?? undefined,

        aggregatorId: '',
        details,
    };

    const lenderRow: DebtProductRowData = {
        ...baseRow,
        lenders: [],
    };

    return {
        ...baseRow,
        lenders: [lenderRow],
    };
}
